import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import { useInterval, useTimeout } from '@/utils/Hooks'
import { withYoutubeUrlToShortsContext } from '../../contexts/YoutubeUrlToShortsContext';
import ShortsApis from '@/utils/Videomonster/Apis/Shorts';
import { ErrorHandler } from '@/utils/Error/Handler';
import { ROUTE_PATH, SHORTS_RENDER_STATE } from '@/utils/Global';
import TokenHelper from "@/utils/DataManager/TokenHelper";
import { CircularProgress } from '@material-ui/core';
import axios from 'axios';
import { GetBaseUrl } from '@/utils/ConfigHelper';
import OneBtnDialog from '@/Common/OneBtnDialog/OneBtnDialog';
import './index.scss'
import { isMobile } from 'react-device-detect';
import MobileAppManager from '@/utils/MobileAppManager';

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

const CanvasVideoPlayer = forwardRef(({
    history,
    title,
    analysisId,
    onlyView,
    context: {
        index,
        videoUrl,
        audioUrl,
        previewImageUrl,
        metadataJson,
        initData,

        isPlaying,
        isPreviewMode,
        isEditor,

        width,
        height,

        videoWrapperRef,
        videoRef,
        videoRect,

        setCanvasId,
        clearCanvas,

        extractReneringDatas,

        handlePlayVideo,
        handleStopVideo,
    },
}, ref) => {
    const [isHideVideoLoadingCover, setIsHideVideoLoadingCover] = useState(false)
    const [isCanPlay, setIsCanPlay] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [isDownloading, setIsDownloading] = useState(false)
    const [shortsId, setShortsId] = useState(null)
    const [shortsState, setShortsState] = useState(SHORTS_RENDER_STATE.EMPTY)
    const [isDialogOpened, setIsDialogOpened] = useState(false)
    const [dialogType, setDialogType] = useState(null)

    useImperativeHandle(ref, () => ({
        playVideo: () => {
            if (!isLoaded || isLoading || isPlaying || !isHideVideoLoadingCover) return;

            handlePlayVideo()
        },
        stopVideo: () => {
            if (!isLoaded || isLoading || !isPlaying || !isHideVideoLoadingCover) return;

            handleStopVideo()
        },
    }))

    useEffect(() => {
        setCanvasId(`fabric-canvas-${index}`)

        if (!onlyView) updateShortsId()

        const video = document.getElementById(`analysis-result-video-${index}`)
        video.addEventListener('loadedmetadata', handleVideoLoadedMetadata)
        video.addEventListener('canplay', handleVideoCanplay)

        return () => {
            video.removeEventListener('loadedmetadata', handleVideoLoadedMetadata)
            video.removeEventListener('canplay', handleVideoCanplay)

            handleStopVideo()
            clearCanvas()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useTimeout(async () => {
        if (isLoaded) {
            handlePlayVideo()
            await sleep(100)
            handleStopVideo()
        }
    }, isLoaded ? 200 : null)

    useTimeout(() => {
        if (!isHideVideoLoadingCover) {
            setIsHideVideoLoadingCover(true)
        }
    }, isCanPlay && !isHideVideoLoadingCover ? 1000 : null)

    useEffect(() => {
        if (!onlyView) updateShortsState()
    }, [shortsId])

    useInterval(async () => {
        try {
            if (onlyView || isLoading) return;

            await updateShortsState();
        }
        catch (e) {
            console.error(e)
        }
    }, (onlyView || isLoading) ? null : 4000)

    const updateShortsId = async () => {
        try {
            const { data: { isExists, id: shortsId } } = await ShortsApis.IsExistsShorts(TokenHelper.GetToken(), { analysisId, analysisIndex: index })
            if (isExists) setShortsId(shortsId)
        }
        catch (e) {
            ErrorHandler(this, null, e, { log: true, alert: false })
        }
        finally {
            setIsLoading(false)
        }
    }

    const updateShortsState = async () => {
        try {
            if (!shortsId) return;

            const { data: { item } } = await ShortsApis.GetShortsItem(TokenHelper.GetToken(), { shortsId })
            setShortsState(item.state)

            if (isDownloading && item.state === SHORTS_RENDER_STATE.FINISH) {
                setIsDownloading(false)
                downloadShortsVideo({
                    url: item.url,
                    videoName: item.name
                })
            }
            else if (isDownloading && item.state === SHORTS_RENDER_STATE.ERROR) {
                setIsDownloading(false)
                handleMessageDialogOpen('waitForSownloadShortsVideo')
            }
        }
        catch (e) {
            ErrorHandler(this, null, e, { log: true, alert: false })
        }
    }

    const downloadShortsVideo = async ({ url, videoName }) => {
        try {
            if (isMobile) {
                const filename = `${videoName}.mp4`
                const fileType = 'video'

                if (MobileAppManager.IsAppAvaliableMethod(MobileAppManager.APP_METHOD_KEY.DOWNLOAD_MULTI)) {
                    MobileAppManager.MultiDownload([{
                        url,
                        filename,
                        fileType,
                    }])
                } else {
                    MobileAppManager.Download({
                        url,
                        filename,
                    })
                }
                return;
            }


            axios.get(`${url}?date=${Date.now().toString()}`, {
                responseType: "blob"
            }).then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.download = `${videoName}.mp4`;
                link.click();
            })
                .catch(e => {
                    if (e instanceof Error) {
                        const { message, name, stack } = e
                        axios.post(`${GetBaseUrl()}/api/system/app-error-log`, {
                            id: 'myvideo',
                            log: JSON.stringify({
                                message,
                                name,
                                stack
                            }),
                            deviceInfo: {}
                        }).catch(() => { })
                    }
                })
        }
        catch (e) {
            console.error(e)
        }
    }

    const handleVideoLoadedMetadata = () => {
        setIsLoaded(true)
    }

    const handleVideoCanplay = () => {
        setIsCanPlay(true)
    }

    const handleClickVideo = () => {
        if (isPreviewMode && videoRef.current) {
            if (videoRef.current.paused) {
                handlePlayVideo()
            } else {
                handleStopVideo()
            }
        }
    }

    const handleClickCustomize = async (e, index) => {
        if (isMobile) {
            handleMessageDialogOpen('editorSupportOnPc')
            return;
        }

        try {
            e.stopPropagation()
            const { data: { isExists, id } } = await ShortsApis.IsExistsShorts(TokenHelper.GetToken(), { analysisId, analysisIndex: index })
            let shortsId = null

            if (isExists) {
                shortsId = id
            }
            else {
                const { data } = await ShortsApis.PostShorts(TokenHelper.GetToken(), { analysisId, analysisIndex: index, name: title })
                shortsId = data.data.id
            }

            history.push(ROUTE_PATH.SHORTS_CUSTOMIZING, {
                index,
                shortsId,
                metadataJson,
                videoUrl,
                audioUrl,
                previewImageUrl,
                initData,
            })
        }
        catch (e) {
            ErrorHandler(this, null, e, { log: true, alert: true })
        }
    }

    const handleClickDownload = async (e, index) => {
        try {
            e.stopPropagation()
            setIsDownloading(true)

            let tempShortsId = shortsId
            if (!tempShortsId) {
                const { data: { isExists, id } } = await ShortsApis.IsExistsShorts(TokenHelper.GetToken(), { analysisId, analysisIndex: index })
    
                if (isExists) {
                    tempShortsId = id
                }
                else {
                    const { data } = await ShortsApis.PostShorts(TokenHelper.GetToken(), { analysisId, analysisIndex: index, name: title })
                    tempShortsId = data.data.id
                }
            }

            const { data: { item } } = await ShortsApis.GetShortsItem(TokenHelper.GetToken(), { shortsId: tempShortsId });
            if (item.state === SHORTS_RENDER_STATE.FINISH) {
                setIsDownloading(false)
                downloadShortsVideo({
                    url: item.url,
                    videoName: item.name
                })
                return;
            }

            const renderingData = await extractReneringDatas()
            const { result } = await ShortsApis.PostRenderingShorts(TokenHelper.GetToken(), {
                shortsId: tempShortsId,
                renderingData
            })
            setShortsId(tempShortsId)
            handleMessageDialogOpen('waitForSownloadShortsVideo')
        }
        catch (e) {
            ErrorHandler(this, null, e, { log: true, alert: true })
            setIsDownloading(false)
        }
    }

    const handleMessageDialogOpen = (dialogType) => {
        setDialogType(dialogType)
        setIsDialogOpened(true)
    }

    const handleMessageDialogClose = (e) => {
        setIsDialogOpened(false)
        setDialogType(null)
    }

    const renderShortsState = () => {
        if (onlyView) return <></>

        let stateElement = null
        if (isLoading || isDownloading) {
            stateElement = <div className="clip-content-state-container">
                <CircularProgress size={25} classes={{ circle: "color-primary" }} />
            </div>
        }
        else if (shortsState === SHORTS_RENDER_STATE.FINISH ||
            shortsState === SHORTS_RENDER_STATE.ERROR ||
            shortsState === SHORTS_RENDER_STATE.EMPTY) {
            stateElement = <>
                <button id="" className={`clip-content-bottom-button ${isMobile ? 'mobile' : ''}`} onClick={(e) => handleClickCustomize(e, index)}>
                    <img src="https://front.videomonster.com/images-u6yd72dy/common/ai/customize/customize.svg" alt="" />
                </button>
                <button className={`clip-content-bottom-button ${isMobile ? 'mobile' : ''}`} onClick={(e) => handleClickDownload(e, index)}>
                    <img src="https://front.videomonster.com/images-u6yd72dy/common/ai/customize/download.svg" alt="" />
                </button>
            </>;
        }
        else {
            stateElement = <>
                <div className="clip-content-state-container">
                    <CircularProgress size={25} classes={{ circle: "color-primary" }} />
                </div>
            </>
        }

        return <div className="button-container">
            {stateElement}
        </div>
    }

    return (
        <>
            <div
                className="canvas-video-player-container"
                style={{ width, height }}
                onClick={handleClickVideo}
            >
                <div
                    ref={videoWrapperRef}
                    className='canvas-video-wrapper'
                >
                    {videoUrl && (
                        <video
                            id={`analysis-result-video-${index}`}
                            style={{
                                position: 'absolute',
                                top: videoRect.y,
                                left: videoRect.x,
                                width: videoRect.width,
                                height: videoRect.height,
                                display: 'block',
                                opacity: isCanPlay ? 1 : 0,
                            }}
                            ref={(element) => { 
                                if (isLoaded) videoRef.current = element
                            }}
                            src={videoUrl}
                            playsInline
                            webkit-playsinline
                        />
                    )}
                    <div className="video-loading-cover" hidden={isHideVideoLoadingCover} />
                    <canvas
                        id={`fabric-canvas-${index}`}
                        style={{
                            pointerEvents: isEditor ? 'all' : 'none'
                        }}
                    />
                </div>
                {renderShortsState()}
            </div>
            {
                isDialogOpened && dialogType && (
                    <OneBtnDialog
                        open={isDialogOpened}
                        closeBtn={handleMessageDialogClose}
                        onButtonClick={handleMessageDialogClose}
                        dialogType={dialogType}
                    />
                )
            }
        </>
    )
})

export default withYoutubeUrlToShortsContext(CanvasVideoPlayer);